import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import { mergeAdjacentRuns, pickRunStyleFields } from '@/lib/text/text-style-engine'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { logger } from '@/utils/logger'
import { affixTextareaEdit, resolveTextareaEdit } from './helpers/resolve-textarea-edit'
import { paragraphsToPlainText } from '@/lib/shapes/shape-text'

/**
 * Apply a surgical text edit to multi-run paragraphs, preserving existing run style boundaries.
 * Existing text retains its original run styles; inserted text uses `insertStyle` merged over
 * the run style at the cursor position, enabling "future characters" pending-style semantics.
 *
 * `deleteStart`/`deleteEnd` are flat-text offsets in the before-edit text (same order as
 * `paragraphsToPlainText`). `insertText` is placed at `deleteStart` after the deletion.
 */
export function applyEditToParagraphs(
  paragraphs: Paragraph[],
  deleteStart: number,
  deleteEnd: number,
  insertText: string,
  insertStyle: TextRunStyleUpdate | null
): Paragraph[] {
  type CharEntry = { char: string; style: Partial<TextRun>; isParaSep: boolean }
  const chars: CharEntry[] = []

  paragraphs.forEach((para, paraIdx) => {
    ;(para.textLines ?? []).forEach((line) => {
      ;(line.textRuns ?? []).forEach((run) => {
        const { textContent, ...style } = run

        for (const char of textContent ?? '') {
          chars.push({ char, style, isParaSep: false })
        }
      })
    })
    if (paraIdx < paragraphs.length - 1) {
      chars.push({ char: '\n', style: {}, isParaSep: true })
    }
  })

  // Style at the cursor position, used as the base for inserted characters.
  const fallbackStyle: Partial<TextRun> = (() => {
    const run = paragraphs[0]?.textLines?.[0]?.textRuns?.[0]

    if (!run) return {}
    const { textContent: _tc, ...s } = run

    return s
  })()
  // The nearest character BEFORE the edit that is not a paragraph break: what a native caret carries
  // forward, and the only style source a break itself can't be (its entry holds `{}`).
  const styleBeforeEdit: Partial<TextRun> | null = (() => {
    for (let i = Math.min(deleteStart, chars.length) - 1; i >= 0; i--) {
      if (!chars[i].isParaSep) return chars[i].style
    }

    return null
  })()
  const entryAtCursor = deleteStart < chars.length ? chars[deleteStart] : chars[chars.length - 1]
  // The run an EMPTY paragraph carries, when the caret is in one (a break or a bound on both sides).
  // It is the style that paragraph keeps and that the caret shows, so typing in it must use it rather
  // than the line above's.
  const emptyParagraphStyle: Partial<TextRun> | null = (() => {
    const at = Math.max(0, Math.min(deleteStart, chars.length))
    const breakBefore = at === 0 || chars[at - 1].isParaSep
    const breakAfter = at >= chars.length || chars[at].isParaSep

    if (!breakBefore || !breakAfter) return null

    const paragraphIdx = chars.slice(0, at).filter((entry) => entry.isParaSep).length
    const run = paragraphs[paragraphIdx]?.textLines?.[0]?.textRuns?.[0]

    if (!run) return null
    const { textContent: _tc, ...style } = run

    return style
  })()
  // A break at the insertion point, or ending the text, has no style of its own. Taking its `{}` typed
  // the first character of a new line at the frame default instead of the size just typed on the line
  // above (72pt text, Enter, 18pt line: WMPCM-11367). An empty line uses its own run first, then the
  // text before the break.
  const cursorStyle: Partial<TextRun> = !entryAtCursor
    ? fallbackStyle
    : entryAtCursor.isParaSep
      ? (emptyParagraphStyle ?? styleBeforeEdit ?? fallbackStyle)
      : entryAtCursor.style

  // Effective style for inserted characters: cursor style merged with pending run-level fields.
  const effectiveStyle: Partial<TextRun> = insertStyle
    ? { ...cursorStyle, ...pickRunStyleFields(insertStyle) }
    : cursorStyle

  // Bounds are clamped EXPLICITLY and loudly. `Array.prototype.splice` clamps silently, so an
  // out-of-range range — offsets resolved against a string the model no longer describes — used
  // to delete the TAIL of the frame with nothing failing anywhere: no throw, no console error,
  // tsc and jest both green. That is the silent-clamp class behind Known Gaps #22-#31 and #35,
  // and it is what made a mid-frame Backspace in a linked chain destroy the frame’s last
  // character instead of the one before the caret. `applyTextareaEditToParagraphs` is the guard
  // that keeps the keystroke path out of that state; this is the backstop that makes it visible
  // if anything else gets here.
  const safeStart = Math.max(0, Math.min(deleteStart, chars.length))
  const safeEnd = Math.max(safeStart, Math.min(deleteEnd, chars.length))

  if (safeStart !== deleteStart || (deleteEnd > deleteStart && safeEnd !== deleteEnd)) {
    logger.warn(
      'shape text: edit range clamped to the model —',
      `asked [${deleteStart},${deleteEnd}), applied [${safeStart},${safeEnd}), length ${chars.length}`
    )
  }

  // Apply the deletion.
  if (safeEnd > safeStart) {
    chars.splice(safeStart, safeEnd - safeStart)
  }

  // Insert new characters with the effective style.
  if (insertText.length > 0) {
    const newChars: CharEntry[] = [...insertText].map((char) => ({
      char,
      style: char === '\n' ? {} : effectiveStyle,
      isParaSep: char === '\n',
    }))

    chars.splice(safeStart, 0, ...newChars)
  }

  // Split on paragraph separators and rebuild paragraphs.
  const groups: Array<Array<{ char: string; style: Partial<TextRun> }>> = [[]]

  for (const c of chars) {
    if (c.isParaSep) {
      groups.push([])
    } else {
      groups[groups.length - 1].push({ char: c.char, style: c.style })
    }
  }

  // Paragraphs this edit's own line breaks opened. An empty one is seeded from where the break was
  // made, not from some other paragraph's first run (`origPara`): the empty line it was pressed on
  // (`emptyParagraphStyle`), else the text before it (`styleBeforeEdit`). So Enter after 72pt text
  // leaves the caret at 72pt, and Enter on an empty 12pt line opens another 12pt line. Empty
  // paragraphs the edit did not create (a deletion emptied them) keep theirs.
  const breakCount = [...insertText].filter((char) => char === '\n').length
  const firstOpenedGroup = chars.slice(0, safeStart).filter((entry) => entry.isParaSep).length + 1
  const isOpenedByBreak = (paraIdx: number) =>
    paraIdx >= firstOpenedGroup && paraIdx < firstOpenedGroup + breakCount

  return groups.map((group, paraIdx) => {
    const origPara = paragraphs[paraIdx] ?? paragraphs[paragraphs.length - 1]

    // Build single-char TextRun list, then merge adjacent same-style runs into the final list.
    const singleCharRuns: TextRun[] = group.map(({ char, style }) => ({
      ...(style as TextRun),
      textContent: char,
    }))
    const runs: TextRun[] =
      singleCharRuns.length > 0
        ? mergeAdjacentRuns(singleCharRuns)
        : [
            (() => {
              const breakStyle = emptyParagraphStyle ?? styleBeforeEdit

              if (isOpenedByBreak(paraIdx) && breakStyle) {
                return { ...(breakStyle as TextRun), textContent: '' }
              }

              const origRun = origPara?.textLines?.[0]?.textRuns?.[0]

              return origRun ? { ...origRun, textContent: '' } : { textContent: '' }
            })(),
          ]

    return {
      ...origPara,
      textLines: [{ textRuns: runs }],
    }
  })
}

/** Throttle for the desync warning below — a held key would otherwise emit one per repeat. */
export let lastDesyncWarnAt = 0

export const DESYNC_WARN_INTERVAL_MS = 2000

/**
 * Apply the edit a textarea just made to the paragraph model, and guarantee the two agree after.
 *
 * `before` is the string the textarea held before the keystroke (its `props.value` — React
 * guarantees the node held it), `after` is `node.value` now, `caretAfter` its post-edit
 * `selectionStart`.
 *
 * Two things this owns that callers must not re-derive:
 *
 * 1. **The offsets are resolved in `before` space and applied in MODEL space.** Those are the same
 *    space only while `paragraphsToPlainText(paragraphs) === before`, and nothing enforces that. In
 *    a linked chain the live projection (`projectChainStoryLive`) rewrites `paragraphs` behind the
 *    editor on every keystroke, and the trim-resync that restores the invariant
 *    (`shape-text-editor/hooks/use-chain-trim-resync.ts`) is best-effort with three early-outs — most notably its bail on a
 *    non-collapsed selection, which a live range selection (the gesture that applies a font change)
 *    holds open indefinitely. While the two are apart, `applyEditToParagraphs`' `splice` clamps and
 *    eats the TAIL of the frame instead of the character before the caret.
 * 2. So the result is **reconciled against `after`** before it is returned. The model always ends up
 *    describing exactly what the user sees typed, per keystroke, instead of depending on an effect
 *    landing in time. On the normal path the reconcile is a no-op, so the return is byte-identical
 *    to calling `applyEditToParagraphs` directly.
 *
 * Resolving `paragraphsToPlainText` inside this module is deliberate, not incidental: a caller that
 * mocks that export cannot reach it from here, so the invariant check cannot be faked away by a
 * test double.
 */
export function applyTextareaEditToParagraphs(
  paragraphs: Paragraph[],
  before: string,
  after: string,
  caretAfter: number | null,
  insertStyle: TextRunStyleUpdate | null
): Paragraph[] {
  const edit = resolveTextareaEdit(before, after, caretAfter)
  const edited = applyEditToParagraphs(
    paragraphs,
    edit.deleteStart,
    edit.deleteEnd,
    edit.insertText,
    insertStyle
  )
  const editedText = paragraphsToPlainText(edited)

  if (editedText === after) return edited

  const now = Date.now()

  if (now - lastDesyncWarnAt > DESYNC_WARN_INTERVAL_MS) {
    lastDesyncWarnAt = now
    // Says what it implies, because this line is the only signal that a resync hole is live: the
    // model had drifted from the buffer the offsets were measured in, and the reconcile above put it
    // back. Distinct from the clamp warning in `applyEditToParagraphs`, which reports a range that
    // arrived out of bounds from somewhere else.
    logger.warn(
      'shape text: model was out of step with the editor buffer and has been reconciled —',
      `model ${editedText.length} chars vs buffer ${after.length}; a chain trim-resync did not catch up`
    )
  }

  const repair = affixTextareaEdit(editedText, after)

  return applyEditToParagraphs(
    edited,
    repair.deleteStart,
    repair.deleteEnd,
    repair.insertText,
    null
  )
}

/**
 * Like `applyEditToParagraphs` but accepts an ordered list of styled runs so that clipboard
 * content can be pasted with per-character style fidelity. Each run carries its own
 * `Partial<TextRun>` style; newline characters (`'\n'`) always receive an empty style and become
 * paragraph separators regardless of the run's declared style.
 */
export function applyStyledInsertToParagraphs(
  paragraphs: Paragraph[],
  deleteStart: number,
  deleteEnd: number,
  insertRuns: Array<{
    textContent: string
    style: Partial<TextRun>
    /**
     * The source paragraph's own alignment/spacing/indentation, on the first run of each pasted
     * paragraph (WMPCM-10131 Issue 2). Without it every pasted paragraph silently adopted
     * `paragraphs[paraIdx]` — the DESTINATION's settings — so centred text pasted left-aligned.
     */
    paraStyle?: Omit<Paragraph, 'textLines'>
  }>
): Paragraph[] {
  type CharEntry = {
    char: string
    style: Partial<TextRun>
    isParaSep: boolean
    /** Set on the first inserted char of a pasted paragraph; absent on destination chars. */
    paraStyle?: Omit<Paragraph, 'textLines'>
    /** True for every char the paste contributed — distinguishes new text from existing. */
    fromPaste?: boolean
  }
  const chars: CharEntry[] = []

  paragraphs.forEach((para, paraIdx) => {
    ;(para.textLines ?? []).forEach((line) => {
      ;(line.textRuns ?? []).forEach((run) => {
        const { textContent, ...style } = run

        for (const char of textContent ?? '') {
          chars.push({ char, style, isParaSep: false })
        }
      })
    })
    if (paraIdx < paragraphs.length - 1) {
      chars.push({ char: '\n', style: {}, isParaSep: true })
    }
  })

  // Same explicit, loud clamp as `applyEditToParagraphs` above, and for the same reason — the
  // paste path resolves its offsets from `ta.selectionStart`/`selectionEnd`, which describe the
  // live buffer rather than the model. See that function for what a silent `splice` clamp costs.
  const safeStart = Math.max(0, Math.min(deleteStart, chars.length))
  const safeEnd = Math.max(safeStart, Math.min(deleteEnd, chars.length))

  if (safeStart !== deleteStart || (deleteEnd > deleteStart && safeEnd !== deleteEnd)) {
    logger.warn(
      'shape text: edit range clamped to the model —',
      `asked [${deleteStart},${deleteEnd}), applied [${safeStart},${safeEnd}), length ${chars.length}`
    )
  }

  // Apply the deletion.
  if (safeEnd > safeStart) {
    chars.splice(safeStart, safeEnd - safeStart)
  }

  const newChars: CharEntry[] = []

  for (const run of insertRuns) {
    let isFirstCharOfRun = true

    for (const char of run.textContent) {
      newChars.push({
        char,
        style: char === '\n' ? {} : run.style,
        isParaSep: char === '\n',
        fromPaste: true,
        // Only the run's first char carries it — the run is the paragraph's opening run, so its
        // first char is the paragraph's first char.
        ...(isFirstCharOfRun && run.paraStyle ? { paraStyle: run.paraStyle } : {}),
      })
      isFirstCharOfRun = false
    }
  }

  if (newChars.length > 0) {
    chars.splice(safeStart, 0, ...newChars)
  }

  type Group = {
    chars: Array<{ char: string; style: Partial<TextRun> }>
    /** Source paragraph style, if the paste opened this paragraph. */
    paraStyle?: Omit<Paragraph, 'textLines'>
    /** True while every char in this paragraph came from the paste. */
    allFromPaste: boolean
  }
  const groups: Group[] = [{ chars: [], allFromPaste: true }]

  for (const c of chars) {
    if (c.isParaSep) {
      groups.push({ chars: [], allFromPaste: true })
    } else {
      const group = groups[groups.length - 1]!

      group.chars.push({ char: c.char, style: c.style })
      if (c.paraStyle && !group.paraStyle) group.paraStyle = c.paraStyle
      if (!c.fromPaste) group.allFromPaste = false
    }
  }

  return groups.map((group, paraIdx) => {
    const origPara = paragraphs[paraIdx] ?? paragraphs[paragraphs.length - 1]
    // The source paragraph's settings win only where they cannot overwrite formatting the user
    // already had: a paragraph the paste wholly created. Paste into the middle of existing text
    // and the destination keeps its own alignment — which is what every editor does, and what
    // "paste into this paragraph" means as opposed to "replace this paragraph".
    //
    // An empty destination paragraph contributes no chars, so it counts as wholly-from-paste —
    // the common case: pasting a centred paragraph into a fresh frame keeps it centred.
    const pastedParagraphStyle = group.allFromPaste && group.paraStyle ? group.paraStyle : undefined
    const singleCharRuns: TextRun[] = group.chars.map(({ char, style }) => ({
      ...(style as TextRun),
      textContent: char,
    }))
    const runs: TextRun[] =
      singleCharRuns.length > 0
        ? mergeAdjacentRuns(singleCharRuns)
        : [
            (() => {
              const origRun = origPara?.textLines?.[0]?.textRuns?.[0]

              return origRun ? { ...origRun, textContent: '' } : { textContent: '' }
            })(),
          ]

    return {
      ...origPara,
      ...pastedParagraphStyle,
      textLines: [{ textRuns: runs }],
    }
  })
}
